import { ArrowLeftOutlined } from "@ant-design/icons";
import {
  Form,
  Button,
  Space,
  Tooltip,
  Typography,
  Input,
  Card,
  Tabs,
  TabsProps,
  Checkbox,
} from "antd";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Account from "../../../models/Account";
import { NavigationContext } from "../../../context/NavigationContext";
import { ToastContext } from "../../../context/ToastContext";
import AccountService from "../../../services/crud/AccountService";
import DescriptionDataList from "../../../components/DescriptionDataList";
import { useForm } from "antd/lib/form/Form";
import { OverlayContext } from "../../../context/OverlayContext";
import { QuestionCircleFilled } from "@ant-design/icons/lib/icons";

import { EntitySelector } from "../../../components/forms/selects/EntitySelector";
import { atom, useRecoilState } from "recoil";
import { SubscriptionsTable } from "./SubscriptionsTable";
import { PlansTable } from "./PlansTable";
import Hub from "../../../models/Hub";
import { Modes } from "../../../Modes";
import UsersTable from "./UsersTable";
import { BookingsTable } from "./BookingsTable";
import { OrganizationButtonForm } from "../../../components/forms/OrganisationForm";
import BookingExternalService, { BookingExternal } from "../../../services/crud/BookingExternalService";
import Booking from "../../../models/Booking";
import UserGroupService from "../../../services/crud/UserGroupService";
import { UserGroup } from "../../../models/UserGroup";
import { HubsTable } from "./HubsTable";
import useTabsSyncWithUrl from "../../../hooks/tabsSyncWithUrl";
import { commaSeparatedListToArray, flattenCommaSeparatedList } from "../../../utils/CommaSeparatedLists";
import UserService from "../../../services/crud/UserService";
import User from "../../../models/User";

const hubsState = atom<Hub[]>({
  key: "company_hubs",
  default: [],
});

export const accountState = atom<Account>({
  key: "company_account",
  default: {} as Account,
});

const Header = () => {
  let navigate = useNavigate();
  const gotoPage = (route) => {
    console.log("route is: " + route);
    navigate(route);
  };
  return (
    <div>
      <Button size="small" icon={<ArrowLeftOutlined />} type="link" onClick={() => gotoPage(-1)}>
        Back to company list
      </Button>
    </div>
  );
};

const ProfileSection = ({ account, bookings, actions = <></> }) => {
  const data = {
    id: account.id,
    name: account.name,
    "White Label": account.whiteLabel?.name ?? "No White Label",
    invited: account.users.filter((u) => !u.isRegistered),
    users: account.users.filter((u) => u.isRegistered),
    // Account original .bookings only contain non-external bookings.
    // so override with unified bookings array in this view that contains
    // all bookings.
    bookings: bookings,
    subscriptions: account.subscriptions,
    hubs: account.hubs,
    tags: account.tags,
    hasBikeShare: account.hasBikeShare,
    hasMabiCarShare: account.hasMabiCarShare,
    hasKintoShare: account.hasKintoShare,
    authorizedDomains: account.authorizedDomains,
    "Default book by invoice": account.defaultAllowBookByInvoice == true ? "Yes" : "No",
  };
  return (
    <div>
      <Card>
        <DescriptionDataList data={data} footer={<Space>{actions}</Space>} />
      </Card>
    </div>
  );
};

const EditProfileForm = ({ account, form }) => {
  return (
    <Form form={form}>
      <Form.Item name="id" label="id" required>
        <Input disabled />
      </Form.Item>
      <Form.Item name="name" label="name" required>
        <Input />
      </Form.Item>
      <Tooltip title="Name of your company">
        <QuestionCircleFilled></QuestionCircleFilled>
      </Tooltip>
      <Form.Item name="whiteLabelId" label="White label">
        <EntitySelector.WhiteLabels></EntitySelector.WhiteLabels>
      </Form.Item>
      <Form.Item name="authorizedDomains" label="Authorized email domains">
        <Input />
      </Form.Item>
      <Tooltip title="Separate domains with comma as: @domain1.se, domain2.com">
        <QuestionCircleFilled></QuestionCircleFilled>
      </Tooltip>
      <Form.Item name="defaultAllowBookByInvoice" label="Default book by invoice" valuePropName="checked" rules={[{ type: "boolean" }]}>
        <Checkbox />
      </Form.Item>
      <Tooltip title="Default value for 'allow book by invoice'. Applies to new users or invited users at the time when they are added/invited.">
        <QuestionCircleFilled></QuestionCircleFilled>
      </Tooltip>
      <Form.Item name="tags" label="tag">
        <EntitySelector.Tag></EntitySelector.Tag>
      </Form.Item>
      <Tooltip title="Members of this account will get the listed tags on their Person in customer.io. Don't use punctuation in the tag names. Use lower case.">
        <QuestionCircleFilled></QuestionCircleFilled>
      </Tooltip>
    </Form>
  );
};

export type CompanyAccountsViewProps = {};
/**
 * This page lists all company accounts.
 * @param param0
 * @returns
 */
function CompanyAccountsView({}: CompanyAccountsViewProps) {
  const [form] = useForm();
  const { openModal, closeModal } = useContext(OverlayContext);
  const { mode } = useContext(NavigationContext);
  const [activeKey, onTabChange] = useTabsSyncWithUrl('users');
  const { id } = useParams<any>();
  const accountId = parseInt(id);
  const [account, setAccount] = useRecoilState(accountState);
  const { loading, loadMessage } = useContext(ToastContext);
  const [bookings, setBookings] = useState<(BookingExternal | Booking)[]>(account.bookings ?? []);
  const [userGroups, setUserGroups] = useState<UserGroup[]|null>(null);
  const users = [
    ...(account?.users ?? []),
  ].sort((a, b) => a.username.toLowerCase().localeCompare(b.username.toLowerCase()));

  const loadAccount = useCallback(async () => {
    if (accountId) {
      await Promise.all([
        AccountService.getAccount(accountId).then((newAccount) => {
          setAccount(newAccount);
        }),
        BookingExternalService.listByAccountId(accountId).then((externalBookings) => {
          setBookings([...externalBookings]);
        }),
        UserGroupService.listUserGroupsOnAccount(accountId).then((userGroups) => {
          UserGroup.sortUserGroupsByLabel(userGroups);
          setUserGroups([...userGroups]);
        }),
      ]);
    }
  }, [accountId]);

  useEffect(() => {
    loadMessage("Loading", () =>
      loadAccount()
        .then(() => "Loaded")
        .catch(() => "error loading ")
    );
  }, [accountId]);

  const handleEdit = () => {
    openEditModal();
  };
  const openEditModal = () => {
    form.resetFields();
    form.setFieldsValue({
      ...account,
      tags: commaSeparatedListToArray(account.tags),
    });
    openModal({
      title: "Edit account profile",
      content: <EditProfileForm account={account} form={form} />,
      onOk: () => {
        form.validateFields().then((values) => {
          values['tags'] = flattenCommaSeparatedList(values['tags'] ?? '');
          values['defaultAllowBookByInvoice'] = values['defaultAllowBookByInvoice'] == true; // two equals intentionally to convert 1 to true.
          loadMessage("Adding item...", async () => {
            await AccountService.editAccount(account?.id, values);
            AccountService.getAccount(account?.id).then((account) => {
              setAccount(account);
            });
            return "Updated account profile";
          });
          closeModal();
        });
      },
    });
  };

  let profileActions = <></>;
  let userTableActions = { updateBalance: false, viewUser: false };

  switch (mode) {
    case Modes.SYSTEM_ADMIN:
      profileActions = (
        <>
          <Button disabled={loading} type="primary" onClick={handleEdit}>
            Edit
          </Button>
          <></>
          <>
            <OrganizationButtonForm accountId={accountId}></OrganizationButtonForm>
          </>
        </>
      );
      userTableActions = { updateBalance: true, viewUser: true };
      break;
    case Modes.COMPANY_ADMIN:
      profileActions = <></>;
      userTableActions = { updateBalance: false, viewUser: false };
      break;
    case Modes.REAL_ESTATE_ADMIN:
      profileActions = <></>;
      userTableActions = { updateBalance: false, viewUser: false };
      break;
  }
  const handleActionsComplete = () => {
    loadAccount();
  };

  const tabItems: TabsProps["items"] = [
    {
      key: "users",
      label: `Users`,
      children: (
        <UsersTable
          dataSource={users ?? []}
          userGroups={userGroups ?? []}
          accountId={account.id}
          onActionComplete={handleActionsComplete}
          actions={userTableActions}
          loading={loading}
          isCompany={true}
        ></UsersTable>
      ),
    },
    {
      key: "bookings",
      label: `Bookings`,
      children: <BookingsTable dataSource={bookings}></BookingsTable>,
    },
    {
      key: "plans-and-subscriptions",
      label: `Plans & subscriptions`,
      children: (
        <view>
          <SubscriptionsTable id={account?.id}></SubscriptionsTable>
          <PlansTable id={account?.id}></PlansTable>
        </view>
      ),
    },
    {
      key: "hubs",
      label: `Hubs`,
      children: <HubsTable id={account?.id} hubsState={hubsState}></HubsTable>,
    },
    {
      key: "profile",
      label: `Company overview`,
      children: <ProfileSection actions={profileActions} bookings={bookings} account={account}></ProfileSection>,
    },
  ];

  return (
    <div>
      <Header></Header>
      <Typography.Title level={1} style={{ marginTop: "15px" }}>
        {account?.name}
      </Typography.Title>
      <Tabs
        type="line"
        style={{ minHeight: "100vh" }}
        defaultActiveKey="users"
        items={tabItems}
        activeKey={activeKey}
        onChange={onTabChange} />
    </div>
  );
}

export default CompanyAccountsView;
